<template>
  <v-app>
    <AppNavigation v-if="isAuthenticated && showAppBar && showNavigation"></AppNavigation>
    <AppBar v-if="isAuthenticated && showAppBar" />
    <v-main>
      <router-view />
    </v-main>
    <InputDialog ref="inputDialog"></InputDialog>
    <InputListDialog ref="inputListDialog"></InputListDialog>
    <ConfirmDialog ref="confirmDialog"></ConfirmDialog>
    <Snackbar ref="snackbar"></Snackbar>
    <WelcomeDialog :close="closeWelcomeDialog" :show="showWelcomeDialog" />
    <div class="pt-0 chat-container" v-if="showChatContainer">
      <ChatContainer />
    </div>
    <img src="/img/powered-by.png" class="powered-by" />
  </v-app>
</template>

<style>
.chat-container {
  position: fixed;
  bottom: 10px;
  right: 10px;
  width: 350px;
  z-index: 2000;
  pointer-events: auto;
}
</style>

<script>
import Snackbar from "@/components/Snackbar.vue";
import InputListDialog from "@/dialogs/InputListDialog.vue";
import ConfirmDialog from "@/dialogs/ConfirmDialog.vue";
import InputDialog from "@/dialogs/InputDialog.vue";
import authService from "@/services/authService.js";
import logoService from "@/services/logoService";
import userService from "@/services/userService";
import companyService from "@/services/companyService";
import AppNavigation from "@/components/AppNavigation.vue";
import AppBar from "@/components/AppBar.vue";
import { API_URL, URL } from "@/env";
import { mapGetters } from "vuex";
import WelcomeDialog from "@/dialogs/WelcomeDialog.vue";
import localStorageService from "@/services/localStorageService.js";
import logAuthException from "@/services/exceptionLoggingService";
import notificationService from "@/services/notificationService";
import notificationSessionService from "@/services/notificationSessionService";
import chatService from "@/services/chatService";
import featuresService from "./services/featuresService";
import ChatContainer from "@/components/chat/ChatContainer.vue";

export default {
  name: "App",

  components: {
    InputListDialog,
    InputDialog,
    Snackbar,
    AppNavigation,
    ConfirmDialog,
    WelcomeDialog,
    AppBar,
    ChatContainer,
  },

  data() {
    return {
      API_URL,
      isUserAuthorized: false,
      showWelcomeDialog: false,
      allDocumentTypesForUser: [],
    };
  },

  computed: {
    showNavigation() {
      return this.$route.name && this.$route.name !== "Document";
    },

    showAppBar() {
      return (
        this.$route.name &&
        this.$route.name !== "LandingPage" &&
        this.$route.name.indexOf("Register") !== 0 &&
        this.$route.name.indexOf("OnBoarding") !== 0 &&
        this.$route.name.indexOf("BoardingCompany") !== 0 &&
        this.$route.name.indexOf("callbakPakservice") !== 0 &&
        this.isUserAuthorized
      );
    },
    isAuthenticated() {
      return this.$auth0 && this.$auth0.isAuthenticated.value && !this.$auth0.isLoading.value;
    },
    showChatContainer() {
      return this.isAuthenticated && this.messagingFlag && this.showAppBar;
    },
    ...mapGetters("auth0Store", ["isGlobalUser", "isOnBoardingPending", "selectedCompanyId"]),
    ...mapGetters("featuresStore", ["messagingFlag"]),
    auth0Loading() {
      return this.$auth0?.isLoading?.value;
    },
  },
  methods: {
    async logout() {
      await chatService.disconnect();
      await authService.logout();
      localStorageService.clear();
      this.$store.dispatch("auth0Store/clearSelectedCompanyId");
      await this.$auth0.logout({ logoutParams: { returnTo: URL } });
    },
    handleBeforeUnload() {
      chatService.disconnect();
    },
    async changePass() {
      await userService.resetPassword();
      this.$root.$snackbar.message("Check your inbox to reset your password");
    },
    naviateToUserProile() {
      this.$router.push({ name: "Profile" });
    },
    showAdmin() {
      this.$router.push({ path: "/admin/users" });
    },
    async closeWelcomeDialog(gotoSettings) {
      this.showWelcomeDialog = false;
      if (gotoSettings) {
        this.$router.push("/admin/logo");
      }
    },
    async initializeNotifications(companies, companyId) {
      let unreadNotifications = await notificationService.getUnreadNotifications();
      this.$store.dispatch("userProfile/setUnreadNotification", unreadNotifications);

      await notificationSessionService.initialize(
        companies,
        this.$root.$snackbar.messageWithUrl,
        this.$root.$snackbar.systemNotification,
        companyId
      );
    },

    async onAuthenticated() {
      // Read from window.location instead of $route.query because the auth0Loading
      // watcher (immediate: true) can fire before Vue Router has populated $route,
      // which silently drops the impersonation override coming from DVC Admin.
      const rawCompanyId = new URLSearchParams(window.location.search).get("selectedCompanyId");
      const parsedId = parseInt(rawCompanyId);
      const overriddenCompanyId = Number.isInteger(parsedId) ? parsedId : null;
      let companies = [];
      try {
        companies = await companyService.getAllCompanies();
        this.$store.dispatch("auth0Store/updateCompanies", companies);
      } catch (companyError) {
        logAuthException.logAuthException(companyError, "Retrieve user companies", "App.vue");
      }

      const candidateCompanyId = overriddenCompanyId || this.selectedCompanyId;
      const isValidCompany = candidateCompanyId && companies.some(c => c.id === candidateCompanyId);
      if (isValidCompany) {
        this.$store.dispatch("auth0Store/updateCompanyId", { selectedCompanyId: candidateCompanyId });
      } else if (candidateCompanyId) {
        this.$store.dispatch("auth0Store/clearSelectedCompanyId");
      }
      const resolvedCompanyId = isValidCompany ? candidateCompanyId : null;

      if (
        companies.length > 0 &&
        companies.every(c => c.isCloud) &&
        decodeURIComponent(window.location.href).includes("admin/invitations")
      ) {
        this.$store.dispatch("auth0Store/updateAuth0Initialized");
        this.isUserAuthorized = true;
        return;
      } else {
        try {
          const exchangeUser = await userService.getUserByExternalId(
            decodeURI(this.$auth0.user.value.sub),
            resolvedCompanyId
          );

          this.$store.dispatch("invitationStore/setPendingInvitations");

          if (resolvedCompanyId != null) {
            await this.initializeNotifications(companies, resolvedCompanyId);

            let company = companies.find(c => c.id === Number(resolvedCompanyId));
            await featuresService.init(company, exchangeUser.user);

          }

          if (!exchangeUser.user.companies.filter(x => !x.isCloud).length) {
            const hasCloudAccess = companies.some(x => x.isCloud);
            if (hasCloudAccess) {
              const cloudCompany = companies.filter(x => x.isCloud)[0];
              const url = cloudCompany.url;
              window.location.href = `${url}/repository`;
            } else {
              document.write(this.$auth0.user.value.email + " is not authorized to use this application.");
              const auth0 = this.$auth0;
              setTimeout(async () => {
                alert(
                  `${auth0.user.value.email} is not authorized for this company. You will be now be logged out and will need to log back in with a different user`
                );
                await authService.logout();
                localStorageService.clear();
                this.$store.dispatch("auth0Store/clearSelectedCompanyId");
                auth0.logout({ logoutParams: { returnTo: window.location.origin } });
              }, 1500);
            }

            return;
          }
          this.isUserAuthorized = true;
          this.$store.dispatch("terminologyStore/updateTerminologies", { terminologies: exchangeUser.terminologies });

          this.$store.dispatch("auth0Store/updateUser", { user: exchangeUser.user });

          this.allDocumentTypesForUser = exchangeUser.documentTypes;
          this.$store.dispatch("documentTypeStore/updateDocumentTypes", {
            documentTypes: this.allDocumentTypesForUser,
          });

          const profile = await userService.getUserPersonalization();
          this.$store.dispatch("userProfile/initUserProfile", profile.items);

          this.$store.dispatch("auth0Store/updateAuth0Initialized");
          if (this.isOnBoardingPending) {
            await this.$root.$confirmDialog.open(
              "Sent Invitation",
              "You Sent an invitation. You need to wait for the company to accept or reject the invitation.",
              "Continue",
              { hideCancel: true }
            );
          }

          this.showWelcomeDialog = exchangeUser.displayWelcomeMessage && this.$route.name === "Home";
        } catch (ex) {
          logAuthException.logAuthException(ex, "Failed login to Exchange", "App.vue");
          this.$router.push({ name: "ErrorLoading" });
        }
      }
      try {
        const logo = await logoService.getLogoAsBase64();
        this.$store.dispatch("logoStore/initLogo", logo);
      } catch (ex) {
        // fail silently when no logo
      }
    },
  },
  watch: {
    async selectedCompanyId(newValue, oldValue) {
      notificationSessionService.setCompanyId(this.selectedCompanyId);

      if (oldValue == null && newValue != null && this.isAuthenticated) {
        let companies = await companyService.getAllCompanies();
        await this.initializeNotifications(companies, newValue);
      } else if (oldValue != null && newValue != null) {
        await chatService.disconnect();
      }
    },
    auth0Loading: {
      handler(isLoading) {
        if (isLoading === false && this.$auth0.isAuthenticated.value) {
          this.onAuthenticated();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$root.$snackbar = this.$refs.snackbar;
    this.$root.$inputListDialog = this.$refs.inputListDialog;
    this.$root.$inputDialog = this.$refs.inputDialog;
    this.$root.$confirmDialog = this.$refs.confirmDialog;
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeUnmount() {
    chatService.disconnect();
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
};
</script>

<style>
.highlight {
  animation: pulse 1s 2;
  background: rgb(var(--v-theme-metadataHighlight));
}

html,
.v-application,
.v-overlay-container {
  font-family: "Lato", sans-serif !important;
}

html :is(.text-h1, .text-h2, .text-h3, .text-h4, .text-h5, .text-h6, .text-subtitle-1, .text-subtitle-2, .text-body-1, .text-body-2, .text-button, .text-caption, .text-overline) {
  font-family: "Lato", sans-serif;
}

.global-user {
  border: 2px solid rgb(var(--v-theme-error));
}

@media only screen and (max-width: 600px) {
  .container {
    padding: 0 !important;
  }
}

.docvue-background {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  position: absolute;
  top: 0;
}

.docvue-background img {
  width: 100%;
}

.powered-by {
  margin: 15px 15px 15px auto;
}

/* Tab bar bottom border */
.v-tabs {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* Brand: 24px page content padding to match design system reference */
.v-main .v-container--fluid {
  padding: 24px;
}

/* Data table header polish */
.v-data-table th {
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  vertical-align: middle !important;
}

/* Row hover */
.v-data-table .v-data-table__tr:hover td {
  background-color: rgb(var(--v-theme-surface-light)) !important;
}
</style>
